import { primitiveColors } from "../../primitives/colors";
import {
  action,
  background,
  border,
  chart,
  icon,
  overlay,
  text,
} from "../../current/colors-figma";
import { themeInvariants } from "./invariants";
import type { WebTheme } from "./theme";

const darkPalette = {
  ui: {
    background: {
      page: background.page,
      pageNavy: background.page,
      pageSubtle: action.destructive.icon,
      inverse: background.inverse,
      inverseSecondary: primitiveColors.neutral200,
      success: background.success,
      error: background.error,
      warning: background.warning,
      segmentRest: "transparent",
      segmentSelected: background.inverse,
      raisedDisabled: background.disabled,
      disabled: background.disabled,
      raised: background.raised,
      raisedTranslucent: "#FFFFFF0F",
      insetTranslucent: primitiveColors.whiteOverlay4,
      deep: background.page,
      inset: background.inset,
      insetDisabled: primitiveColors.neutral600,
      glass: "#15191C80",
      info: background.info,
      savings: primitiveColors.extendedForest950,
      gain: background.gain,
    },
    text: {
      primary: text.primary,
      secondary: text.secondary,
      tertiary: text.tertiary,
      disabled: text.disabled,
      inverse: text["primary-inverse"],
      secondaryInverse: text["secondary-inverse"],
      onColor: border.focus,
      onBrand: action.primary.text,
      onFill: text["primary-inverse"],
      brand: "#8CA1EE",
      primaryOnPage: text.primary,
      accent: "#B9C4EE",
      success: text.success,
      error: text.error,
      warning: text.warning,
      info: text.link,
    },
    icon: {
      primary: icon.primary,
      secondary: icon.secondary,
      tertiary: border.strong,
      disabled: icon.disabled,
      inverse: icon["primary-inverse"],
      onColor: border.focus,
      brand: icon.brand,
      success: icon.success,
      error: icon.error,
      warning: icon.warning,
      info: icon.link,
      onFill: icon["primary-inverse"],
    },
    border: {
      default: border.default,
      subtle: border.subtle,
      strong: border.strong,
      focus: border.focus,
      focusInverse: action.destructive.icon,
      selected: border.selected,
      brand: action.secondary.background,
      brandSoft: primitiveColors.blue600Alpha40,
      successSoft: primitiveColors.green900Alpha40,
      error: border.error,
      success: border.success,
      disabled: border.disabled,
      warning: border.warning,
      info: border.link,
      strongPlus: primitiveColors.neutral400Alpha14,
      divider: border.disabled,
    },
    interactive: {
      primary: action.primary.background,
      primaryIcon: action.primary.icon,
      primaryForeground: action.primary.background,
      primaryDeepTintSubtle: "rgba(30, 79, 221, 0.07)",
      primaryDeepTint: primitiveColors.blue600Alpha10,
      primaryDeepTintMedium: "rgba(30, 79, 221, 0.12)",
      primaryDeepTintHeavy: "#1E4FDD33",
      primaryDeepTintStrong: primitiveColors.blue600Alpha14,
      cur8Brand: action.primary.background,
      glassStrong: primitiveColors.whiteOverlay12,
      primaryActive: "#FFFFFF33",
      selected: "#FFFFFF33",
      secondary: action.secondary.background,
      secondaryBorder: action.secondary.border,
      secondarySubtle: action.secondary["background-subtle"],
      secondaryText: action.secondary.text,
      secondaryIcon: action.secondary.icon,
      destructive: action.destructive.background,
      destructiveText: action.destructive.text,
      destructiveIcon: action.destructive.icon,
      disabled: "#30363D",
      disabledText: "#6E7781",
      disabledIcon: "#6E7781",
      primaryPressed: primitiveColors.green400,
      destructivePressed: primitiveColors.red400,
      inverse: action.inverse.background,
      inversePressed: primitiveColors.white,
      inverseIcon: action.inverse.icon,
      neutral: action.neutral.background,
      neutralPressed: primitiveColors.neutral825,
      neutralBorder: action.neutral.border,
      neutralText: action.neutral.text,
      neutralIcon: action.neutral.icon,
    },
    feedback: {
      success: action.primary.background,
      error: primitiveColors.red400,
      warning: border.warning,
      pending: "#FFF5AB",
      info: action.secondary.border,
      notification: action.destructive.background,
      gaugeFill: primitiveColors.green650,
    },
    base: {
      black: "#000000",
    },
    overlay: {
      light: "#00000033",
      medium: "#00000066",
      dark: "#000000CC",
      black: "#000000",
      transparent: "transparent",
      scrim: overlay.overlay,
    },

    gradient: {
      iconBadge: {
        start: primitiveColors.glassIconStart,
        end: primitiveColors.glassIconEnd,
      },
      dividerAction: {
        line: primitiveColors.neutral300Alpha30,
      },
    },
  },
  domainSpecific: {
    numbers: {
      positive: text.gain,
      positiveIcon: icon.gain,
      negative: text.loss,
      negativeIcon: icon.loss,
      neutral: primitiveColors.slate900,
    },
    entityTypes: {
      personal: text.personal,
      personalIcon: icon.personal,
      personalBg: primitiveColors.whiteOverlay8,
      ifisa: text.isa,
      ifisaIcon: icon.isa,
      ifisaBg: primitiveColors.green400Alpha12,
      business: primitiveColors.extendedVioletDark,
      businessText: text.business,
      businessIcon: icon.business,
      businessBg: primitiveColors.whiteOverlay8,
      chipNeutral: background.neutral,
      chipPersonal: background.personal,
      chipIsa: background.isa,
      chipBusiness: background.business,
    },
    timelineTiles: {
      warning: background.warning,
      neutral: primitiveColors.neutral825,
      success: background.gain,
      danger: background.error,
      info: action.secondary["background-subtle"],
    },
    dealTypes: {
      fixedIncome: {
        active: primitiveColors.extendedPeach,
        inactive: text["secondary-inverse"],
      },
      ventureCapital: {
        active: primitiveColors.extendedCoral,
        inactive: text["secondary-inverse"],
      },
      realEstate: {
        active: primitiveColors.extendedRose,
        inactive: text["secondary-inverse"],
      },
      privateEquity: {
        active: primitiveColors.extendedLavender,
        inactive: text["secondary-inverse"],
      },
      fallback: {
        active: primitiveColors.extendedSalmon,
        inactive: border.strong,
      },
    },
    badgeTypes: {
      success: {
        background: primitiveColors.green900,
        text: text.success,
      },
      warning: {
        background: "#332D00",
        text: text.warning,
      },
      error: {
        background: primitiveColors.red900,
        text: text.error,
      },
      info: {
        background: primitiveColors.blue950,
        text: "#8CA1EE",
      },
      neutral: {
        background: background.disabled,
        text: text.secondary,
      },
      premium: {
        background: primitiveColors.gold800,
        text: primitiveColors.gold200,
      },
      successSubtle: {
        background: primitiveColors.green900Alpha12,
        text: action.primary.background,
      },
      errorSubtle: {
        background: primitiveColors.red800Alpha12,
        text: text.error,
      },
      accent: {
        background: primitiveColors.blue600Alpha14,
        text: "#B9C4EE",
      },
      neutralSubtle: {
        background: primitiveColors.neutral400Alpha12,
        text: text.secondary,
      },
    },
    callout: {
      neutral: {
        background: primitiveColors.whiteOverlay10,
        text: text.primary,
      },
      success: {
        background: primitiveColors.green900Alpha20,
        text: text.success,
      },
      warning: {
        background: primitiveColors.amber500Alpha12,
        text: text.warning,
      },
      error: {
        background: "#7A180033",
        text: text.error,
      },
    },
    premium: {
      accent: "#D1BA82",
      background: primitiveColors.gold800,
      text: primitiveColors.gold200,
      pendingBg: primitiveColors.amber500Alpha8,
      pendingBorder: primitiveColors.amber500Alpha20,
      pendingText: primitiveColors.amber500,
      pendingTextMuted: primitiveColors.amber500Alpha65,
      pendingAccent: primitiveColors.amber500,
    },
  },
  chart: {
    series1: chart["data-1"],
    series2: chart["data-2"],
    series3: chart["data-3"],
    series4: chart["data-4"],
    series5: chart["data-5"],
    gain: icon.gain,
    loss: action.destructive.background,
  },
} as const;

export const darkWebColors = {
  ...darkPalette,
  ui: {
    ...darkPalette.ui,
    text: { ...darkPalette.ui.text, brandSoft: primitiveColors.blue600Alpha40 },
  },
} as const;

export const webDarkTheme: WebTheme = {
  mode: "dark",
  colors: darkWebColors,
  ...themeInvariants,
};
